import { useCallback, useEffect } from 'react';

import { bulletinboardsApi, BulletinMeta } from 'apis/bulletinboards';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

/** This is the hard limit on how many bulletins a user can add */
export const MAX_ALLOWED_BULLETINS = 9;

export type UseBulletinsReturn = {
  /** Is bulletin list is being fetched */
  isFetching: boolean;
  /** Are any of the bulletins (or the list) updating */
  isUpdating: boolean;
  /** Has there been an error loading the list of bulletins */
  hasReadError: boolean;
  /** The list of bulletins */
  bulletins: readonly BulletinMeta[];
  /**
   * Function to update the bulletins enabled property, will update bulletin and
   * send api request.
   *
   * @param bulletin Bulletin to update
   * @param enabled New enabled setting
   */
  setEnabled: (bulletin: BulletinMeta, enabled: boolean) => void;
  /**
   * Function to delete the bulletin, will update bulletins list and send api
   * request.
   *
   * @param bulletin Bulletin to remove
   */
  onDelete: (bulletin: BulletinMeta) => Promise<void>;
  /**
   * Function to navigate to the bulletin editor.
   *
   * @param bulletin Bulletin selected to edit
   */
  onEdit: (bulletin: BulletinMeta) => void;
  /** Are you allowed to add more bulletins */
  canAddMore: boolean;
  /** Create a new bulletin and redirect to bulletin editor */
  onAdd: () => void;
  /**
   * Called when bulletin list has been reordered, bulletins will have order
   * property updated and api request will be made.
   *
   * @param bulletins Reordered list of bulletins
   */
  onReorder: (bulletins: BulletinMeta[]) => void;
};

type Params = {
  boardId: string;
  messengerId: string;
  /** Called before adding a new bulletin */
  onBeforeAdd?: () => void;
};

export function useBulletins({
  boardId,
  messengerId,
  onBeforeAdd,
}: Params): UseBulletinsReturn {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const notify = useNotifications();
  const [updateBulletin, { isLoading: isUpdateLoading }] =
    bulletinboardsApi.useUpdateBulletinMutation();
  const [deleteBulletin, { isLoading: isRemoveLoading }] =
    bulletinboardsApi.useDeleteBulletinMutation();
  const [createBulletin, { isLoading: isCreateLoading }] =
    bulletinboardsApi.useCreateBulletinMutation();

  const notifyError = useCallback(
    (message: string) => notify({ title: message, type: 'error' }),
    [notify],
  );

  const { isFetching, isError, data } = bulletinboardsApi.useReadBulletinsQuery(
    {
      boardId,
    },
  );

  useEffect(() => {
    if (isError) {
      notifyError(
        'Sorry there has been problem loading your bulletins, please try again.',
      );
    }
  }, [isError, notifyError]);

  const bulletins = data?.data.bulletins ?? [];

  const onDelete = async (bulletin: BulletinMeta): Promise<void> => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete content',
            description: `Are you sure you want to delete ${bulletin.title} This action can’t be undone.`,
            confirm: {
              label: 'Yes, delete',
            },
          },
        },
      }),
    );

    if (!isConfirmed) return;

    try {
      await deleteBulletin({ boardId, bulletinId: bulletin.id });
    } catch (error) {
      notifyError(
        'Sorry, there has been an error deleting your bulletin, please try again.',
      );
    }
  };

  const setEnabled = async (bulletin: BulletinMeta, active: boolean) => {
    try {
      await updateBulletin({
        boardId,
        bulletinId: bulletin.id,
        update: {
          active,
        },
      });
    } catch (error) {
      notifyError('Updating bulletin failed, please try again');
    }
  };

  const onEdit = (bulletin: BulletinMeta) => {
    navigate({
      path: '/settings/dixa-messenger/appearance/:messengerId/bulletin/:bulletinId',
      params: { path: { bulletinId: bulletin.id, messengerId } },
    });
  };

  const onAdd = async () => {
    try {
      onBeforeAdd?.();

      const order =
        bulletins.length > 0 ? bulletins[bulletins.length - 1].order + 1 : 1;

      const resp = await createBulletin({ boardId, title: '', order });

      if ('data' in resp) {
        const newBulletin = resp.data;
        navigate({
          path: '/settings/dixa-messenger/appearance/:messengerId/bulletin/:bulletinId',
          params: { path: { bulletinId: newBulletin.id, messengerId } },
        });
      }
    } catch (error) {
      notifyError('Creating bulletin failed');
    }
  };

  const onReorder = async (bulletins: BulletinMeta[]) => {
    const updatedBulletins = bulletins.map((b, i) => ({
      ...b,
      order: i + 1,
    }));

    const promises = updatedBulletins.map((bulletin) => {
      return updateBulletin({
        boardId,
        bulletinId: bulletin.id,
        update: {
          order: bulletin.order,
        },
      });
    });

    try {
      await Promise.all(promises);
    } catch (error) {
      notifyError('Updating order failed, please try again');
    }
  };

  return {
    hasReadError: isError,
    isFetching,
    isUpdating: isCreateLoading || isUpdateLoading || isRemoveLoading,
    bulletins,
    canAddMore: bulletins.length < MAX_ALLOWED_BULLETINS,
    setEnabled,
    onDelete,
    onEdit,
    onAdd,
    onReorder,
  };
}
